import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Container, Grid, makeStyles } from '@material-ui/core';
import { createStyles } from '@material-ui/core/styles';
import { MAX_BUYERS, MAX_LENDERS, MAX_SELLERS } from 'api/file/constants';
import { GetFileInfoProps, saveFile, verifyAddress } from 'api/file/file-api';
import { getLenderListFromFirmID } from 'api/file/file-party-api';
import { ICreateFileResponse } from 'api/file/interfaces/create-file';
import { IVerifyAddressResponse } from 'api/file/interfaces/verify-address';
import { generateReport } from 'api/prismPropertyProfile/prismProperty-profile-api';
import { PrismGenerateReport } from 'api/prismPropertyProfile/types';
import { getTitleMonitoringFlyerByPropertyId } from 'api/titleMonitoring/titleMonitoring-api';
import { getStateOptions, sortLenders } from 'api/profile/profile-utilities';
import { getFileDocument } from 'api/sdn/sdn-api';
import {
  DateFormat,
  DocumentType,
  OFACPdfWindowName,
  SdnPdfWindowName,
  WindowFeatures,
  newWindow,
  PREMIUM_TAX_CODES,
  STATE_CODES,
} from 'features/constants';
import PropertyGroupWithAgentNetBase from 'features/files/file-property/PropertyGroupWithBaseFormGroup';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import sdnPolling from 'hooks/sdnPolling';
import useScrollToAndFocus from 'hooks/useScrollToElement';
import * as _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { isObjectEmpty, openDocument, removeEmpty } from 'utilities/utilities';
import { fileInfoEditFormSchema } from 'utilities/validation/schemas';
import { deletePropertyFromList } from '../../../api/file/file-property-api';
import { fileInfoType, propertyType } from '../../../api/file/interfaces/get-file';
import { FileDataContext, FileDataContextInterface } from '../../../hooks/FileDataContext';
import useAsync from '../../../hooks/useAsync';
import ofacPdf from '../../../static/NA_Standard_OFAC_Underwriting_Communication_Agents.pdf';
import { AgentNetConfirmationDialog } from '../../../ui-kit/components/modal/ConfirmationDialog';
import VerifiedPropertiesDrawer from '../file-property/VerifiedPropertiesDrawer';
import { addressVerifyCheckForMatch, apnVerifyCheckForMatch, isPropertyReportExceptionState } from '../file-utilities';
import { IGetBuyerSellerResponse } from '../parties/interfaces/CreateBuyerSellerRequest';
import { IGetLenderResponse } from '../parties/interfaces/CreateLenderRequest';
import { ILenderFirm } from '../parties/interfaces/LenderModels';
import { Parties } from '../parties/parties';
import './FileInformation.scss';
import FileInfoEditFormWithBaseFormGroup from './form/FileInfoEditFormWithBaseFormGroup';
import { useTitleMonitoringInvites } from './titleMonitoring/useTitleMonitoringInvites';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    formContainer: {
      [theme.breakpoints.up('sm')]: {
        minWidth: 200,
      },
    },
    addPropertyBtn: {
      display: 'flex',
      margin: pxToRem(24, 0),
    },
    sdnResultsLoadingContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    sdnResults: {
      border: 'none',
      background: 'transparent',
      padding: 0,
      fontSize: pxToRem(14),
      lineHeight: pxToRem(20),
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    pdfIcon: {
      padding: 0,
      marginLeft: theme.spacing(1),
    },
  }),
);
type FileInformationProps = {
  isPropertyAdded?: any;
  isAdmin?: boolean;
  getFileApiStatus?: 'idle' | 'pending' | 'success' | 'error';
  getFileError: string[];
};

const FileInformation = ({
  isPropertyAdded,
  isAdmin,
  getFileApiStatus,
  getFileError,
}: FileInformationProps): JSX.Element => {
  const { fileId: currentFileId } = useParams<GetFileInfoProps>();
  const openSdnPDF = async (fileId: string, accountNumber: number | undefined) => {
    setSearchDocument(true);
    const token = await getAccessToken();
    const pdf = await getFileDocument(fileId as string, accountNumber as number, token);
    openDocument(pdf, DocumentType, SdnPdfWindowName, WindowFeatures);
    setSearchDocument(false);
  };
  const [isGetDocument, setSearchDocument] = useState(false);

  const [isPending, setPendingFlag] = useState(false);
  const [isTitleMonitoringLoading, setIsTitleMonitoringLoading] = useState(false);
  const [showGenerateReportNotification, setShowGenerateReportNotification] = useState<boolean>(false);
  const [generateReportError, setGenerateReportError] = useState<string[]>([]);
  const [showGenerateReportFailed, setShowGenerateReportFailed] = useState<boolean>(false);

  const formatDate = (date: string) => {
    return moment(date).format(DateFormat);
  };
  const openOfacPDF = () => {
    window.open(ofacPdf, OFACPdfWindowName, WindowFeatures);
  };

  const { getAccessToken, signOut } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();

  const isDisabledPdfIcon = () => {
    return !fileData?.sdnSearchDate;
  };

  const timer = useRef<number | null>(null);
  const classes = useStyles();
  const [currentPropertyId, setCurrentPropertyId] = useState<string>('');
  const [currentPropertyIndex, setCurrentPropertyIndex] = useState<number>(-1);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [isRootFormValid, setIsRootFormValid] = useState<boolean>(true);
  const [subformGroupValidityMap, setSubformGroupValidityMap] = useState<Record<string, boolean>>({});
  const [isFileUpdated, setFileUpdated] = useState<boolean>(false);
  const [isPartiesUpdated, setPartiesUpdated] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [showDeletePropertyNotification, setShowDeletePropertyNotification] = useState<boolean>(false);
  const [fileChanged, setFileChanged] = useState<boolean>(false);

  const [userHasInteracted, setUserHasInteracted] = useState<boolean>(false);
  const [nextLocation, setNextLocation] = useState<string>();
  const [submitAttempted, setSubmitAttempted] = useState<boolean>();
  const [showSaveFailedNotification, setShowSaveFailedNotification] = useState<boolean>();
  const [showSaveSuccessNotification, setShowSaveSuccessNotification] = useState<boolean>();
  const [saveFileLoader, setSaveFileLoader] = useState<boolean>(false);
  const [saveConfirmFileLoader, setSaveConfirmFileLoader] = useState<boolean>(false);
  // TODO: We shouldn't have a separate state for this. Consider refactoring.
  const [currentFile, setCurrentFile] = useState<fileInfoType>({} as fileInfoType);
  const [pollingToken, setPollingToken] = useState('');
  const [parties, setParties] = useState<any>({});

  const [verifiedPropertiesByIndex, setVerifiedPropertiesByIndex] = useState<Record<number, IVerifyAddressResponse[]>>(
    {},
  );
  const [isSaveValidate, setIsSaveValidate] = useState<boolean>(false);

  const [propertiesUpdated, setPropertiesUpdated] = useState<boolean>(false);
  const [cancelModel, setCancelModel] = useState<boolean>(false);
  const [isPartyValid, setIsPartyValid] = useState<Record<string, boolean>>({});
  const [isFileReady, setIsFileReady] = useState<boolean>(false);
  const [fileInfoError, setFileInfoError] = useState<string[]>([]);
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const fileSection = useRef(null);
  const propertiesRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);

  const partiesRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);
  const maxPartyErrorRef = useRef(null);

  const {
    fileData,
    setFileData,
    openSaveModal,
    setOpenSaveModal,
    isFormDirty,
    setIsFormDirty,
    logoutAttempted,
    setLogoutAttempted,
    setOpenCreateDrawer,
    setOpenRatesAndFeeDrawer,
    isCreateDrawerClicked,
    isRatesAndFeeDrawerClicked,
    setRatesAndFeeDrawerClicked,
    setIsCreateDrawerClicked,
    setTabValue,
    setResetFileNoDisable,
    setOfficeDisabled,
    setUnderwriterDisabled,
    loadFileOrderInsightsData,
    setSessionFileId,
  } = fileDataCtx;
  const history = useHistory();
  const [properties, setProperties] = useState(fileData?.properties ?? []);
  const {
    invitesByPropertyId: titleMonitoringInvitesByPropertyId,
    isTitleMonitoringAvailable,
    isLoadingInvites: isLoadingTitleMonitoringInvites,
    refreshInvites: refreshTitleMonitoringInvites,
  } = useTitleMonitoringInvites(properties);
  const isTitleMonitoringPending = useRef(false);
  const [openPartiesAccordian, setPartiesAccordian] = useState([]);
  const [savingFailedError, setSavingFailedError] = useState<string[]>([]);
  const disableInputs = () => {
    setResetFileNoDisable && setResetFileNoDisable(true);
    setOfficeDisabled && setOfficeDisabled(true);
    setUnderwriterDisabled && setUnderwriterDisabled(true);
  };
  const fileId = fileData?.fileId ?? '';
  const DEFAULT_TOASTER_TIMEOUT = 6000;

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, profile, prismUserProfile } = profileCtx;

  const getLenderFirmList = async (): Promise<ILenderFirm[]> => {
    const token = await getAccessToken();
    const firmId = userFirm?.firmId ?? '';
    return await getLenderListFromFirmID(firmId as string, token);
  };
  const [lenderfirms, setLenderFirms] = useState<
    {
      name: string;
      value: string;
      createdDate: number;
    }[]
  >([]);

  const {
    execute: executeGetLenderFirms,
    value: lenderFirmList,
    status: getLenderStatus,
  } = useAsync<ILenderFirm[]>(getLenderFirmList, false);

  useEffect(() => {
    executeGetLenderFirms();
  }, [userFirm?.firmId]);

  const sortedLenderFirms = React.useMemo(() => {
    if (lenderFirmList) {
      return sortLenders([...lenderFirmList]);
    }
    return [];
  }, [lenderFirmList]);

  useEffect(() => {
    setLenderFirms(sortedLenderFirms);
  }, [sortedLenderFirms]);

  const { setStartPolling, setDelay, pollingResult, hasPollingResult } = sdnPolling(pollingToken, fileData, null);
  useEffect(() => {
    if (hasPollingResult) {
      setFileData &&
        setFileData({
          ...fileData,
          buyers: pollingResult?.buyers,
          sellers: pollingResult?.sellers,
          lenders: pollingResult?.lenders,
          sdnSearchDate: pollingResult?.searchDate,
        });
      setFileUpdated(true);
    }
  }, [hasPollingResult]);

  useEffect(() => {
    if (fileData?.properties) {
      const tempPropertyList =
        fileData?.properties.map((property: any) => {
          property.expand = property.expand ?? false;
          return property;
        }) ?? [];
      setProperties(tempPropertyList);
    }
  }, [fileData?.properties]);

  const deleteProperty = async (): Promise<propertyType> => {
    const token = await getAccessToken();
    return await deletePropertyFromList(fileId ?? '', currentPropertyId ?? '', token);
  };

  const evaluateSubformValidity = () =>
    isObjectEmpty(subformGroupValidityMap as Record<string, unknown>) ||
    Object.keys(subformGroupValidityMap).every((subform) => subformGroupValidityMap[subform]);

  const evaluatePartyValidity = () =>
    isObjectEmpty(isPartyValid) || Object.keys(isPartyValid).every((subform: any) => isPartyValid[subform]);

  const isFormValid = evaluateSubformValidity() && isRootFormValid && evaluatePartyValidity();

  const resetValidation = () => {
    setIsPartyValid({});
    setSubformGroupValidityMap({});
    setIsRootFormValid(true);
  };
  const handleOpenAccordian = useCallback((value) => {
    setPartiesAccordian(value);
  }, []);

  const handleScroll = (currentElement: any) => {
    if (currentElement) {
      const headerHeight = 70;
      const elementRect = currentElement.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollDistance = elementRect.top + scrollTop - headerHeight;
      const rootElement = document.getElementById('root');
      if (rootElement) rootElement.scrollBy({ top: scrollDistance, behavior: 'smooth' });
    }
  };

  const handleScrollToNew = (currentElement: any) => {
    if (currentElement) {
      const elementRect = currentElement.getBoundingClientRect();
      const scrollDistance = window.innerHeight - elementRect.top;
      const rootElement = document.getElementById('root');
      if (rootElement) rootElement.scrollBy({ top: scrollDistance, behavior: 'smooth' });
    }
  };

  const openErrorAccordians = (partyLimitReached: boolean) => {
    setProperties((prevProperties: propertyType[]) => {
      const tempProperties = prevProperties.map((item: propertyType) => {
        if (subformGroupValidityMap[item.propertyCustomId ?? item.propertyId] === false) {
          return { ...item, expand: true };
        }
        return item;
      });
      return tempProperties;
    });

    const partyValidaty = !evaluatePartyValidity();
    const propertyValidaity = !evaluateSubformValidity();
    if (partyValidaty) {
      handleOpenAccordian(Object.keys(isPartyValid));
    }

    if (!isRootFormValid) {
      // autoscroll fileinfo
      handleScroll(fileSection?.current);
    } else if (propertyValidaity) {
      // autoscroll property
      const getFirstProperty = Number(Object.keys(subformGroupValidityMap)[0]);
      handleScroll(propertiesRefs?.current[getFirstProperty]);
    } else if (!partyLimitReached) {
      // autoscroll party max error
      handleScroll(maxPartyErrorRef.current);
    } else if (partyValidaty) {
      // autoscroll party
      const getFirstParty = Number(Object.keys(isPartyValid)[0]);
      handleScroll(partiesRefs?.current[getFirstParty]);
    }

    const msg: string[] = [];

    if (!isRootFormValid) {
      msg.push('File Info details error');
    }
    if (propertyValidaity) {
      msg.push('Property details error');
    }
    if (partyValidaty || !partyLimitReached) {
      // autoscroll party
      msg.push('Party details error');
    }
    setSavingFailedError([...msg]);
  };

  const [showMaxPartyError, setMaxPartyError] = useState({
    buyers: false,
    sellers: false,
    lenders: false,
  });

  const isMaxParty = () => {
    const isMaxBuyers = parties?.buyers?.length > MAX_BUYERS;
    const isMaxSellers = parties?.sellers?.length > MAX_SELLERS;
    const isMaxLenders = parties?.lenders?.length > MAX_LENDERS;

    setMaxPartyError((prevFileData: any) => ({
      ...prevFileData,
      buyers: isMaxBuyers,
      sellers: isMaxSellers,
      lenders: isMaxLenders,
    }));

    return !(isMaxBuyers || isMaxSellers || isMaxLenders);
  };

  // Common address validation logic
  const performAddressValidation = async (): Promise<{
    hasManualValidation: boolean;
    propertiesNeedingManualValidation: Record<number, IVerifyAddressResponse[]>;
    validationErrors: string[];
  }> => {
    if (!fileData?.properties || fileData.properties.length === 0) {
      return {
        hasManualValidation: false,
        propertiesNeedingManualValidation: {},
        validationErrors: [],
      };
    }

    const validationErrors: string[] = [];
    const propertiesNeedingManualValidation: Record<number, IVerifyAddressResponse[]> = {};

    for (let i = 0; i < fileData.properties.length; i++) {
      const property = fileData.properties[i];

      if (property.propertyId) {
        // If not a new property then skip validation.
        continue;
      }

      try {
        const token = await getAccessToken();
        const verifyResponse = await verifyAddress(property, token);

        if (!verifyResponse || verifyResponse.length === 0) {
          validationErrors.push(
            `The address you entered for property ${
              i + 1
            } could not be verified. Please check for errors or continue with the unverified address.`,
          );
          property.propertyDetailId = 0;
          continue;
        }

        // If we get exactly one match, auto-apply it
        if (verifyResponse.length === 1) {
          const validatedProperty = verifyResponse[0];

          if (property.propertyIdentificationMethod === 'apn') {
            if (apnVerifyCheckForMatch(property, validatedProperty)) {
              // Update property with validated data
              property.address1 = property.address1 || validatedProperty.address1;
              property.address2 = property.address2 || validatedProperty.address2;
              property.city = property.city || validatedProperty.city;
              property.postalCode = property.postalCode || validatedProperty.zip;
              property.propertyDetailId = validatedProperty.propertyDetailId;
              continue;
            }
          } else {
            if (addressVerifyCheckForMatch(property, validatedProperty)) {
              // Update property with validated data
              property.apn = validatedProperty.apn;
              property.propertyDetailId = validatedProperty.propertyDetailId;
              continue;
            }
          }
        }

        verifyResponse.unshift({
          address1: property.address1 ?? '',
          address2: property.address2 ?? '',
          city: property.city ?? '',
          stateCode: property.state ?? '',
          zip: property.postalCode ?? '',
          county: property.county ?? '',
          apn: property?.apn ?? '',
          propertyDetailId: 0,
        });

        setIsSaveValidate(true);
        propertiesNeedingManualValidation[i] = verifyResponse;
      } catch (error) {
        validationErrors.push(
          `The address you entered for property ${
            i + 1
          } could not be verified. Please check for errors or continue with the unverified address.`,
        );
        property.propertyDetailId = 0;
      }
    }

    // Update file data with validated properties
    if (setFileData) {
      setFileData({ ...fileData, properties: fileData.properties });
    }

    return {
      hasManualValidation: Object.keys(propertiesNeedingManualValidation).length > 0,
      propertiesNeedingManualValidation,
      validationErrors,
    };
  };

  const handleSaveFile = async () => {
    if (!submitAttempted) {
      setSubmitAttempted(true);
    }
    const isPartyLimitReached = isMaxParty();

    if (isPartyLimitReached && isFormValid) {
      setSaveFileLoader(true);

      // Perform address validation on all properties
      const validationResult = await performAddressValidation();

      if (validationResult.validationErrors.length > 0) {
        // Show validation errors
        validationResult.validationErrors.forEach((error) => {
          addSnackbarMessage({
            html: true,
            title: 'Address Not Validated',
            message: error,
            type: 'error',
          });
        });
      }

      // If there are properties needing manual validation, show the drawer
      if (validationResult.hasManualValidation) {
        setVerifiedPropertiesByIndex(validationResult.propertiesNeedingManualValidation);
        return;
      }

      // Proceed with save after validation
      executeSaveFile();
    } else {
      openErrorAccordians(isPartyLimitReached);
      setSaveFileLoader(false);
      showToaster(setShowSaveFailedNotification);
    }
  };

  const updateSdnStatus = async () => {
    const token = await getAccessToken();
    setPollingToken(token);
    setStartPolling(true);
    setDelay(7000);
  };

  const saveFileData = async (): Promise<ICreateFileResponse> => {
    const token = await getAccessToken();
    setPollingToken(token);
    setStartPolling(true);
    setDelay(7000);
    return await saveFile({ ...fileData, ...parties }, fileId as string, token);
  };

  /* Handle PROPERTY API REQUESTS and its values */

  const {
    execute: executeDeleteProperty,
    value: deletedProperty,
    status: deletedPropertyStatus,
    errors: deletedPropertyError,
  } = useAsync<propertyType>(deleteProperty, false);

  /* Handle UPDATE FILE REQUESTS and its values  */
  const {
    execute: executeSaveFile,
    value: editFile,
    status: saveFileStatus,
    errors: saveFileErrors,
  } = useAsync<ICreateFileResponse>(saveFileData, false);
  const showToaster = useCallback(
    (notificationHook: (show: boolean) => void, duration: number = DEFAULT_TOASTER_TIMEOUT) => {
      notificationHook(true);
      changeValueWithDelay(notificationHook, duration, false);
    },
    [],
  );
  const fileDataDefaultValue = useRef<ICreateFileResponse | null>(null);

  const changeValueWithDelay = useCallback(
    (notificationHook: (show: boolean) => void, duration: number = DEFAULT_TOASTER_TIMEOUT, value = false) => {
      setTimeout(() => {
        notificationHook(value);
      }, duration);
    },
    [],
  );

  useEffect(() => {
    if (deletedPropertyStatus === 'error') {
      handleCloseDialog();
    }
    if (deletedPropertyStatus === 'success') {
      loadFileOrderInsightsData && loadFileOrderInsightsData(fileId ?? '');
    }
  }, [deletedPropertyStatus]);

  const handleFailureSavePrompt = (showSavingFailed: boolean) => {
    setTabValue && setTabValue('0');
    setNextLocation('');
    setSaveConfirmFileLoader(false);
    if (showSavingFailed) showToaster(setShowSaveFailedNotification);
    setLogoutAttempted?.(false);
    setIsCreateDrawerClicked?.(false);
    setOpenSaveModal?.(false);
    setRatesAndFeeDrawerClicked?.(false);
  };
  /* Error Handling for file info */

  const showMessage = (errorMessage: string[]) => {
    errorMessage?.map((err) => {
      addGlobalMsg({
        message: err,
        type: 'error',
      });
    });
    handleFailureSavePrompt(false);
    setFileInfoError([]);
    const isDirty =
      getFileApiStatus !== 'pending' && userHasInteracted && (fileChanged || propertiesUpdated || isPartiesUpdated);
    setIsFormDirty && setIsFormDirty(isDirty);
  };

  useEffect(() => {
    if (Array.isArray(saveFileErrors) && saveFileErrors.length > 0 && saveFileStatus === 'error') {
      setFileInfoError([...saveFileErrors]);
    }

    if (saveFileStatus === 'success') {
      loadFileOrderInsightsData && loadFileOrderInsightsData(fileId ?? '');
    }
  }, [saveFileErrors]);

  useEffect(() => {
    if (Array.isArray(getFileError) && getFileError.length > 0 && getFileApiStatus === 'error') {
      setFileInfoError([...getFileError]);
    }
  }, [getFileError, getFileApiStatus]);

  useEffect(() => {
    if (Array.isArray(deletedPropertyError) && deletedPropertyError.length > 0 && deletedPropertyStatus === 'error') {
      setFileInfoError([...deletedPropertyError]);
    }
  }, [deletedPropertyError, deletedPropertyStatus]);

  useEffect(() => {
    if (fileInfoError.length > 0) showMessage(fileInfoError);
  }, [fileInfoError]);

  /*
   Handle save confirmation model by watching for changes in history.
  */
  useEffect(() => {
    if (isFormDirty) {
      history.block((prompt) => {
        if (!prompt.pathname.includes('/files/file-info')) {
          setNextLocation(prompt.pathname);
          if (timer.current === null) {
            setOpenSaveModal && setOpenSaveModal(true);
          } else {
            navigate();
          }
        }
        return 'true';
      });
    } else {
      history.block(() => undefined);
    }

    return () => {
      history.block(() => undefined);
    };
  }, [history, isFormDirty]);

  const checkPropertyUpdated = (propertyData: any, tempProperty: any) => {
    let isPropertySame = true;
    if (propertyData) {
      const defaultProperty = _.cloneDeep(propertyData);
      defaultProperty.forEach((property: any) => {
        delete property.expand;
      });
      const tempPropertyList = _.cloneDeep(tempProperty);

      tempPropertyList.forEach((property: any) => {
        delete property.expand;
      });
      isPropertySame = _.isMatch(defaultProperty, tempPropertyList);
    }
    setPropertiesUpdated(!isPropertySame);
  };

  const handlePropertiesValidity = (valid: any, key: any) => {
    setSubformGroupValidityMap((prev: any) => {
      const tempForm: any = { ...prev, [key]: valid as boolean };
      return Object.fromEntries(Object.entries(tempForm).filter(([k, v]) => !v)) as any;
    });
  };

  useEffect(() => {
    if (deletedProperty) {
      const tempPropertyList = properties.filter((property) => property.propertyId != currentPropertyId);
      const updatedFileInfo: fileInfoType = {
        ...fileData,
        properties: tempPropertyList ?? ([] as propertyType[]),
      } as fileInfoType;
      if (fileDataDefaultValue.current?.properties) {
        fileDataDefaultValue.current.properties = fileDataDefaultValue.current?.properties.filter(
          (property) => property.propertyId != currentPropertyId,
        );
      }
      window.sessionStorage.setItem('properties', JSON.stringify(updatedFileInfo.properties));
      handlePropertiesValidity(true, currentPropertyId);
      checkPropertyUpdated(fileDataDefaultValue.current?.properties, tempPropertyList);
      setFileData && setFileData(updatedFileInfo);
      handleCloseDialog();
      showToaster(setShowDeletePropertyNotification);
    } else if (deletedProperty !== null) {
      setIsSubmitted(false);
    }
  }, [deletedProperty]);

  const isKyTaxCodeNotFound = (currentProperties: any) => {
    let found = false;
    if (currentProperties) {
      currentProperties.forEach((property: any) => {
        if (
          (property?.taxCode === PREMIUM_TAX_CODES.UNDETERMINED_KY ||
            property?.kyTaxCodeResult?.taxCode === PREMIUM_TAX_CODES.UNDETERMINED_KY) &&
          fileData?.propertyState === STATE_CODES.KY
        ) {
          found = true;
        }
      });
    }

    if (found) {
      addGlobalMsg({
        message: 'Unable to determine match for the Kentucky Premium Tax Code. Please manually update.',
        type: 'warning',
        variant: 'standard',
      });
    }
  };

  useEffect(() => {
    if (isFileReady) {
      isKyTaxCodeNotFound(fileData?.properties);
    }
  }, [isFileReady]);

  useEffect(() => {
    if (fileData && !isObjectEmpty(fileData as Record<string, unknown>)) {
      if (currentFile.fileId !== fileData.fileId) {
        sessionStorage.removeItem('fileId');
        sessionStorage.removeItem('accountNumber');
        setIsFormDirty && setIsFormDirty(false);
        setCurrentFile(_.cloneDeep(fileData));
        setIsFileReady(false);
        return;
      } else if (
        currentFile.fileId &&
        currentFile.fileId === fileData.fileId &&
        !fileDataDefaultValue.current &&
        getFileApiStatus !== 'pending'
      ) {
        fileDataDefaultValue.current = _.cloneDeep(fileData);
        sessionStorage.setItem('accountNumber', (fileData?.accountId ?? 0).toString());
        setIsFileReady(true);
      } else if (
        getFileApiStatus !== 'pending' &&
        fileDataDefaultValue.current?.fileId &&
        fileDataDefaultValue.current?.fileId !== fileData.fileId
      ) {
        fileDataDefaultValue.current = _.cloneDeep(fileData);
        sessionStorage.setItem('accountNumber', (fileData?.accountId ?? 0).toString());
        setIsFileReady(true);
      }
      const isDirty =
        getFileApiStatus !== 'pending' && userHasInteracted && (fileChanged || propertiesUpdated || isPartiesUpdated);
      setIsFormDirty && setIsFormDirty(isDirty);

      if (
        fileData?.fileId &&
        ((fileData?.buyers !== undefined && fileData.buyers.length > 0) ||
          (fileData?.sellers !== undefined && fileData.sellers.length > 0) ||
          (fileData?.lenders !== undefined && fileData.lenders.length > 0)) &&
        (fileData?.sdnSearchDate === undefined || fileData?.sdnSearchDate === null || fileData?.sdnSearchDate === '')
      ) {
        updateSdnStatus();
      }
      return;
    } else {
      setIsFormDirty && setIsFormDirty(false);
      return;
    }
  }, [fileData, currentFile, userHasInteracted, fileChanged, propertiesUpdated, getFileApiStatus, isPartiesUpdated]);

  const checkSSNorTaxId = (item: any, currentParty: any) => {
    const patterns = ['__-_______', '___-__-____'];
    if (item?.entity && Object.keys(item.entity).length > 0) {
      if (patterns.includes(item.entity?.taxId as string) && !currentParty.entity?.taxId) {
        delete item.entity.taxId;
      }
      currentParty;
      if (!currentParty.entity?.entityType && !item.entity?.entityType) {
        delete item.entity.entityType;
      }
      if (!currentParty.entity?.incorporatedState && !item.entity?.incorporatedState) {
        delete item.entity.incorporatedState;
      }
    }

    if (patterns.includes(item.trustEstate?.taxId as string) && !currentParty.trustEstate?.taxId) {
      delete item.trustEstate.taxId;
    }
    if (patterns.includes(item.individual?.ssn as string) && !currentParty.individual?.ssn) {
      delete item.individual.ssn;
    }
    if (item.couple) {
      if (patterns.includes(item.couple?.spouse1?.ssn as string) && !currentParty.couple?.spouse1?.ssn)
        delete item.couple.spouse1.ssn;
      if (patterns.includes(item.couple?.spouse2?.ssn as string) && !currentParty.couple?.spouse1?.ssn)
        delete item.couple.spouse2.ssn;
    }
  };

  const checkPartiesUpdated = React.useCallback((buyer: any, seller: any, lender: any, tempParties: any) => {
    let isPartiesSame = true;

    buyer.forEach((item: IGetBuyerSellerResponse) => {
      item.partyMethod = 'buyer';
      delete item.expand;
    });
    seller.forEach((item: IGetBuyerSellerResponse) => {
      item.partyMethod = 'seller';
      delete item.expand;
    });
    lender.forEach((item: IGetLenderResponse) => {
      item.partyMethod = 'lender';
      delete item.expand;
    });
    const partiesData = [...buyer, ...seller, ...lender];
    if (partiesData) {
      const defaultParty = _.cloneDeep(partiesData);
      const tempPartiesList = _.cloneDeep(tempParties);

      tempPartiesList.forEach((party: any) => {
        // need to check correct party type for all of them
        delete party.expand;
        if (party.partyMethod === 'lender') {
          delete party.partyType;
        } else {
          const currentParty: any[] = defaultParty.filter((item) => item.partyId === party.partyId);
          if (currentParty.length > 0) checkSSNorTaxId(party, currentParty[0]);
          if (party.address) {
            if (
              party.address.isForeignAddress === false &&
              typeof currentParty[0]?.address?.isForeignAddress === 'undefined'
            ) {
              delete party.address.isForeignAddress;
            }
            if (Object.keys(party.address).length === 0) {
              delete party.address;
            }
          }
        }
      });

      const cleanDefaultParty = removeEmpty(defaultParty);
      const cleanTempPartiesList = removeEmpty(tempPartiesList);
      ((cleanTempPartiesList || []) as any[])?.forEach((party: any) => {
        if (party.address && Object.keys(party.address).length === 0) {
          delete party.address;
        }
      });
      isPartiesSame = _.isEqual(cleanDefaultParty, cleanTempPartiesList);
    }
    setPartiesUpdated(!isPartiesSame);
  }, []);

  useEffect(() => {
    if (editFile) {
      fileDataDefaultValue.current = _.cloneDeep(editFile);
      setCurrentFile(_.cloneDeep(editFile) as fileInfoType);
      window.sessionStorage.setItem('properties', JSON.stringify(editFile?.properties ?? []));
      setFileData && setFileData(_.cloneDeep(editFile) as fileInfoType);
      setIsPartyValid({});
      isKyTaxCodeNotFound(editFile.properties);
      showToaster(setShowSaveSuccessNotification);
      setPropertiesUpdated(false);
      setPartiesUpdated(false);
      setFileChanged(false);
      setIsFormDirty && setIsFormDirty(false);
      setSubmitAttempted(false);
      setOpenSaveModal && setOpenSaveModal(false);
      setFileUpdated(true);
      refreshTitleMonitoringInvites();
      executeGetLenderFirms();
      const isDrawerTriedToOpen = isCreateDrawerClicked || isRatesAndFeeDrawerClicked;
      if (isDrawerTriedToOpen) {
        if (isCreateDrawerClicked) {
          setIsCreateDrawerClicked?.(false);
          setOpenCreateDrawer?.(true);
        } else {
          setRatesAndFeeDrawerClicked?.(false);
          setOpenRatesAndFeeDrawer?.(true);
        }
      } else if (logoutAttempted) {
        setLogoutAttempted && setLogoutAttempted(false);
        signOut().then();
        sessionStorage.clear();
        localStorage.clear();
      } else if (nextLocation) {
        navigate();
      }
      setFileInfoError([]);
      disableInputs();

      changeValueWithDelay(setSaveFileLoader, 1000, false);
      changeValueWithDelay(setSaveConfirmFileLoader, 1000, false);
    } else if (editFile !== null) {
      setOpenSaveModal && setOpenSaveModal(false);
      setIsFormDirty && setIsFormDirty(true);
      showToaster(setShowSaveFailedNotification);
      disableInputs();
      changeValueWithDelay(setSaveFileLoader, 1000, false);
      changeValueWithDelay(setSaveConfirmFileLoader, 1000, false);
    }
  }, [editFile]);

  useEffect(() => {
    if (saveFileStatus === 'error') {
      disableInputs();
      setSaveFileLoader(false);
      setSaveConfirmFileLoader(false);
      setOpenSaveModal && setOpenSaveModal(false);
    }
  }, [saveFileStatus]);

  const handleSaveConfirm = useCallback(async () => {
    if (!submitAttempted) {
      setSubmitAttempted(true);
    }
    const isPartyLimitReached = isMaxParty();

    if (isPartyLimitReached && isFormValid) {
      setSaveConfirmFileLoader(true);
      setIsFormDirty && setIsFormDirty(false);

      // Perform address validation on all properties
      const validationResult = await performAddressValidation();

      if (validationResult.validationErrors.length > 0) {
        // Show validation errors
        validationResult.validationErrors.forEach((error) => {
          addSnackbarMessage({
            html: true,
            message: `<b class="savingFailedErrorHeading">${error}</b>`,
            type: 'error',
          });
        });
      }

      // If there are properties needing manual validation, show the drawer
      if (validationResult.hasManualValidation) {
        setSaveConfirmFileLoader(false);
        setIsFormDirty && setIsFormDirty(true);
        setVerifiedPropertiesByIndex(validationResult.propertiesNeedingManualValidation);
        return;
      }

      executeSaveFile();
      if (!logoutAttempted && !isCreateDrawerClicked && nextLocation) {
        history.push(nextLocation);
      }
    } else {
      openErrorAccordians(isPartyLimitReached);
      handleFailureSavePrompt(true);
    }
  }, [
    history,
    parties,
    isFormValid,
    isPartyValid,
    isRootFormValid,
    subformGroupValidityMap,
    nextLocation,
    logoutAttempted,
    isCreateDrawerClicked,
    fileData,
  ]);

  const navigate = useCallback(() => {
    setIsFormDirty?.(false);
    setOpenSaveModal?.(false);

    /* Don't need to navigate to the file-info page, we're already there. Otherwise, go. */
    if (!nextLocation?.includes('/files/file-info')) {
      setLogoutAttempted?.(false);
      setIsCreateDrawerClicked?.(false);
      setRatesAndFeeDrawerClicked?.(false);
      /*
       * setTimeout is used here to fix a hard-to-diagnose problem that blocked the navigation from happening.
       * Without it, it wouldn't go, maybe because other stuff on the page was still executing?
       * */
      timer.current = window.setTimeout(() => {
        nextLocation && history.push(nextLocation);
        timer.current = null;
      }, 0);
    }
  }, [history, nextLocation, openSaveModal, isFormDirty]);

  /* Handle Dialog Modal */
  const handleCloseDialog = () => {
    setIsSubmitted(false);
    setOpenDeleteModal(false);
    setCurrentPropertyId('');
    setCurrentPropertyIndex(-1);
  };

  const [lastItemRef, focusLastItem] = useScrollToAndFocus<HTMLDivElement>({ verticalOffset: 190 });

  const handleAddNewProperty = () => {
    if (properties.length < 5) {
      const defaultAddress = {
        address1: '',
        city: '',
        county: '',
        postalCode: '',
        propertyType: properties[0]?.propertyType ?? 'Residential (1-4 family)',
        state: fileData?.propertyState,
        expand: true,
        propertyCustomId: Date.now(),
        propertyIdentificationMethod: 'address',
      };
      setPropertiesUpdated(true);
      setSubmitAttempted(false);
      setUserHasInteracted(true);
      setFileData && setFileData({ ...fileData, properties: [...properties, defaultAddress] });
      setProperties([...properties, defaultAddress]);
      handleScrollToNew(fileSection?.current);
    }
  };

  useEffect(() => {
    if (properties.length > 0) {
      const lastProperty = properties[properties.length - 1];
      if (lastProperty && lastProperty.address1 === '') {
        const timer = setTimeout(() => {
          focusLastItem();
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [properties, focusLastItem]);

  const [propertExpand, setPropertExpand] = useState(false);
  const handleExpand = () => {
    setPropertExpand(!propertExpand);
    const tempPropertList = properties.map((property: any) => {
      property.expand = propertExpand;
      return property;
    });
    setProperties([...tempPropertList]);
  };

  const handlePropertyDelete = (index: number, propertyId: string) => {
    if (propertyId) {
      setCurrentPropertyId(propertyId);
    } else {
      setCurrentPropertyIndex(index);
    }
    setOpenDeleteModal(true);
  };

  const handleGenerateReport = async (propertyIndex: number) => {
    const property = properties[propertyIndex];
    if (property && !isPending) {
      const stateOptions = userFirm?.offices ? getStateOptions(userFirm) : [];
      const names = stateOptions.map((option) => option.name)?.join(',');
      const officeAddress = userFirm?.offices?.find(
        (office) => office.isDefault && office.status === 'Active',
      )?.address;

      if (!prismUserProfile || names?.length == 0) {
        return;
      }

      prismUserProfile.availableLocations = names;
      prismUserProfile.firmName = userFirm?.name ?? '';
      prismUserProfile.officeAddress1 = officeAddress?.address1 ?? '';
      prismUserProfile.officeAddress2 = officeAddress?.address2 ?? '';
      prismUserProfile.officeCity = officeAddress?.city ?? '';
      prismUserProfile.officeState = officeAddress?.state ?? '';
      prismUserProfile.officeZipCode = officeAddress?.postalCode ?? '';

      const generateReportPayload: PrismGenerateReport = {
        propertyReportUser: prismUserProfile,
        streetNumber: property.address1?.split(' ')[0]?.trim() || '',
        streetName: property.address1?.split(' ').slice(1).join(' ')?.trim() || '',
        unitNumber: property.address2,
        city: property.city || '',
        county: property.county || '',
        state: fileData?.propertyState || '',
        zipCode: property.postalCode?.slice(0, 5) || '',
        propertyDetailId: property.propertyDetailId || 0,
        apn: property.apn || '',
      };

      setPendingFlag(true);
      const token = await getAccessToken();
      const response = await generateReport(generateReportPayload, token);

      if (response?.Error) {
        setGenerateReportError([response.Error]);
      }

      setPendingFlag(false);
      if (response?.ReportPdfBytes) {
        showToaster(setShowGenerateReportNotification);
        openDocument(response?.ReportPdfBytes, DocumentType, newWindow, WindowFeatures);
      }
    }
  };

  const handleTitleMonitoring = async (propertyIndex: number) => {
    const property = properties[propertyIndex];
    const propertyId = Number(property?.propertyId);

    /* The flyer opens in a new window, so a second click while one is in flight would spawn a duplicate */
    if (!Number.isFinite(propertyId) || propertyId <= 0 || isTitleMonitoringPending.current) {
      return;
    }

    isTitleMonitoringPending.current = true;
    setIsTitleMonitoringLoading(true);
    try {
      const token = await getAccessToken();
      const flyer = await getTitleMonitoringFlyerByPropertyId(propertyId, token);

      if (!flyer?.fileContent) {
        throw new Error('Title Monitoring flyer response did not include a document');
      }

      openDocument(flyer.fileContent, DocumentType, newWindow, WindowFeatures, flyer.fileName);
    } catch (error) {
      addSnackbarMessage({
        title: 'Title Monitoring Flyer Not Generated',
        message: 'The Title Monitoring flyer could not be downloaded. Please try again.',
        type: 'error',
      });
    } finally {
      isTitleMonitoringPending.current = false;
      setIsTitleMonitoringLoading(false);
    }
  };

  const handleValidateAddress = async (propertyIndex: number) => {
    let verifyResponse: IVerifyAddressResponse[] = [];
    setSubmitAttempted(true);
    if (fileData?.properties && setFileData) {
      const propertyData = fileData.properties[propertyIndex];

      try {
        const token = await getAccessToken();
        verifyResponse = await verifyAddress(propertyData, token);
      } catch (error) {
        addSnackbarMessage({
          html: true,
          title: 'Address Not Validated',
          message:
            'The address you entered could not be verified. Please check for errors or continue with the unverified address.',
          type: 'error',
        });
        propertyData.propertyDetailId = 0;
        setFileData({ ...fileData });
        return;
      }

      if (!verifyResponse || Object.keys(verifyResponse).length === 0 || verifyResponse.length === 0) {
        addSnackbarMessage({
          html: true,
          title: 'Address Not Validated',
          message:
            'The address you entered could not be verified. Please check for errors or continue with the unverified address.',
          type: 'error',
        });
        propertyData.propertyDetailId = 0;
        setFileData({ ...fileData });
        return;
      } else {
        if (verifyResponse.length === 1) {
          if (propertyData.propertyIdentificationMethod === 'apn') {
            if (apnVerifyCheckForMatch(propertyData, verifyResponse[0])) {
              propertyData.address1 = propertyData.address1 ? propertyData.address1 : verifyResponse[0].address1;
              propertyData.address2 = propertyData.address2 ? propertyData.address2 : verifyResponse[0].address2;
              propertyData.city = propertyData.city ? propertyData.city : verifyResponse[0].city;
              propertyData.postalCode = propertyData.postalCode ? propertyData.postalCode : verifyResponse[0].zip;
              propertyData.propertyDetailId = verifyResponse[0].propertyDetailId;

              setFileData({ ...fileData });
              addSnackbarMessage({
                html: true,
                title: 'Address Validated',
                message: 'The address you entered has been verified as correct and complete.',
                type: 'success',
              });
              return;
            }
          } else {
            if (addressVerifyCheckForMatch(propertyData, verifyResponse[0])) {
              propertyData.apn = verifyResponse[0].apn;
              propertyData.propertyDetailId = verifyResponse[0].propertyDetailId;
              setFileData({ ...fileData });
              addSnackbarMessage({
                html: true,
                title: 'Address Validated',
                message: 'The address you entered has been verified as correct and complete.',
                type: 'success',
              });
              return;
            }
          }
        }

        verifyResponse.unshift({
          address1: propertyData.address1 ?? '',
          address2: propertyData.address2 ?? '',
          city: propertyData.city ?? '',
          stateCode: propertyData.state ?? '',
          zip: propertyData.postalCode ?? '',
          county: propertyData.county ?? '',
          apn: propertyData?.apn ?? '',
          propertyDetailId: 0,
        });

        setIsSaveValidate(false);
        setVerifiedPropertiesByIndex({ [propertyIndex]: verifyResponse });
      }
    }
  };

  useEffect(() => {
    showSaveSuccessNotification &&
      addSnackbarMessage({
        message: `Saving Successful`,
        type: 'success',
      });
  }, [showSaveSuccessNotification]);

  useEffect(() => {
    let list = '';
    savingFailedError.forEach((msg: string) => {
      list += `<li>${msg}</li>`;
    });
    showSaveFailedNotification &&
      addSnackbarMessage({
        html: true,
        message: `<b class="savingFailedErrorHeading">Saving Failed</b>
            ${
              list
                ? `<ul class="savingFailedErrorList">
              ${list}
            </ul>`
                : ''
            }`,
        type: 'error',
        onClose: () => {
          setShowSaveFailedNotification(false);
        },
      });
  }, [showSaveFailedNotification]);

  useEffect(() => {
    showDeletePropertyNotification &&
      addSnackbarMessage({
        message: `Property Deleted Successfully`,
        type: 'success',
        onClose: () => {
          setShowDeletePropertyNotification(false);
        },
      });
  }, [showDeletePropertyNotification]);

  useEffect(() => {
    showGenerateReportNotification &&
      addSnackbarMessage({
        title: 'Property Report Generated Successfully',
        message: 'Report available for the next 24 hours',
        type: 'success',
        onClose: () => {
          setShowGenerateReportNotification(false);
        },
      });
  }, [showGenerateReportNotification]);

  useEffect(() => {
    const errorMessages = generateReportError.join(', ');
    const isGlobalError = errorMessages.toLowerCase().includes('please call');
    if (isGlobalError) {
      showGenerateReportFailed &&
        addGlobalMsg({
          message: `${errorMessages}`,
          type: 'error',
        });
    } else {
      showGenerateReportFailed &&
        addSnackbarMessage({
          title: 'Property Report Not Generated',
          message: `<div class="f-14">${errorMessages}</div>`,
          type: 'error',
          html: true,
          onClose: () => {
            setShowGenerateReportFailed(false);
          },
        });
    }
  }, [showGenerateReportFailed]);

  useEffect(() => {
    if (generateReportError.length > 0) showToaster(setShowGenerateReportFailed);
  }, [generateReportError]);

  const toggleCancelModel = useCallback(() => {
    setCancelModel((prevData) => !prevData);
  }, []);

  const isFileLoaded = useMemo(
    () => !(isFileReady && fileDataDefaultValue?.current?.fileId === currentFileId),
    [isFileReady, fileDataDefaultValue, currentFileId],
  );

  const handlePartyUpdate = useCallback((value) => {
    setPartiesUpdated(value);
  }, []);

  const handleFileUpdate = useCallback((value) => {
    setFileUpdated(value);
  }, []);

  const handleFileInfoError = useCallback((value) => {
    setFileInfoError(value);
  }, []);

  const handlePartyValid = useCallback((value) => {
    setIsPartyValid(value);
  }, []);

  const handleMaxPartyLimit = useCallback((value) => {
    setMaxPartyError(value);
  }, []);
  const handleSubmitted = useCallback((value) => {
    setSubmitAttempted(value);
  }, []);

  const handleParties = useCallback((newValueOrFunction) => {
    setParties((prevValue: any) => {
      if (typeof newValueOrFunction === 'function') {
        return newValueOrFunction(prevValue);
      } else {
        return newValueOrFunction;
      }
    });
  }, []);
  const BottomFooter = React.useMemo(() => {
    return (
      isFormDirty && (
        <BottomAppBar
          primaryActionLabel="Save Changes"
          primaryActionDataQa="FileInfoSave"
          cancelDataQa="FileInfoCancel"
          onPrimaryClick={handleSaveFile}
          disablePrimary={saveFileLoader}
          onCancel={toggleCancelModel}
          primaryButtonLoader={saveFileLoader}
        />
      )
    );
  }, [
    parties,
    isFormDirty,
    isPartyValid,
    saveFileLoader,
    fileData,
    isFormValid,
    isRootFormValid,
    subformGroupValidityMap,
    submitAttempted,
  ]);

  const sdnResultsMessage = useMemo(
    () => (
      <>
        <LoadingSpinner status={isGetDocument ? 'pending' : 'success'} variant="linear" />
        Up to 8 Buyer/Borrowers, 8 Seller/Owners, and 4 Lenders permitted.
      </>
    ),
    [isGetDocument],
  );

  const handleOpenSdnPDF = useCallback(() => openSdnPDF(fileId, fileData?.accountId), [fileId, fileData?.accountId]);

  const PartiesSection = React.useMemo(() => {
    return (
      isFileReady && (
        <Parties
          isFileUpdated={isFileUpdated}
          isAddPartyDisabled={isFileLoaded}
          handleSubmitted={handleSubmitted}
          setIsFormValid={handlePartyValid}
          lenderList={lenderfirms}
          getLenderStatus={getLenderStatus}
          defaultValue={fileDataDefaultValue.current}
          lenderFirmList={lenderFirmList ?? ({} as ILenderFirm[])}
          showAllValidation={submitAttempted}
          setPartiesUpdated={handlePartyUpdate}
          setFileUpdated={handleFileUpdate}
          setFileInfoError={handleFileInfoError}
          checkPartiesUpdated={checkPartiesUpdated}
          handleParties={handleParties}
          showMaxPartyError={showMaxPartyError}
          handleMaxPartyLimit={handleMaxPartyLimit}
          showToaster={showToaster}
          openPartiesAccordian={openPartiesAccordian}
          handleOpenAccordian={handleOpenAccordian}
          maxPartyErrorRef={maxPartyErrorRef}
          partiesRefs={partiesRefs}
          sdnResultsMessage={sdnResultsMessage}
          openSdnPDF={handleOpenSdnPDF}
        />
      )
    );
  }, [
    currentFileId,
    isFileReady,
    isFileUpdated,
    submitAttempted,
    lenderFirmList,
    getLenderStatus,
    fileDataDefaultValue?.current,
    lenderfirms,
    isFileLoaded,
    showMaxPartyError,
    openPartiesAccordian,
    sdnResultsMessage,
  ]);

  const resetFileInfo = useCallback(
    _.debounce(() => {
      resetValidation();
      setPropertiesUpdated(false);
      setPartiesUpdated(false);
      setFileChanged(false);
      setSubmitAttempted(false);
      setFileUpdated(true);
    }, 1000),
    [],
  );

  return (
    <div
      ref={fileSection}
      onClick={() => setUserHasInteracted(true)}
      onKeyUp={() => setUserHasInteracted(true)}
      className={classes.root}
    >
      <LoadingSpinner status={isPending || isTitleMonitoringLoading ? 'pending' : 'success'} variant="linear" />

      <Container className="file-information-container">
        <FileInfoEditFormWithBaseFormGroup
          showAllValidation={submitAttempted}
          value={fileData ?? ({} as fileInfoType)}
          onChange={(newFileData: fileInfoType) => {
            if (fileDataDefaultValue.current && isFileReady && fileDataDefaultValue.current.fileId === currentFileId) {
              // Remove Unwanted Data
              const tempFileData = _.cloneDeep(newFileData);
              delete tempFileData.properties;
              delete tempFileData.sdnSearchDate;
              delete tempFileData.buyers;
              delete tempFileData.sellers;
              delete tempFileData.lenders;
              delete tempFileData.remainingParties;
              if (!tempFileData.closingDate && !fileDataDefaultValue.current.closingDate) {
                delete tempFileData.closingDate;
              }
              if (!tempFileData.loanAmount && !fileDataDefaultValue.current.loanAmount) {
                delete tempFileData.loanAmount;
              }
              if (!tempFileData.salesPrice && !fileDataDefaultValue.current.salesPrice) {
                delete tempFileData.salesPrice;
              }
              const isFileSame = _.isMatch(fileDataDefaultValue.current, tempFileData);
              setFileChanged(!isFileSame);
              if (newFileData.lenders && newFileData.lenders[0]) {
                newFileData.lenders[0].loanAmount = newFileData?.loanAmount ?? 0;
              }
            } else {
              setFileChanged(false);
            }
            newFileData && setFileData && setFileData({ ...newFileData });
          }}
          schema={fileInfoEditFormSchema}
          setIsFormValid={setIsRootFormValid}
        />

        <PageHeader
          title="Properties"
          titleVariant="h3"
          disableMargin="x"
          divider={false}
          contentRight={
            <AgentNetButton
              variant="contained"
              color="primary"
              onClick={handleAddNewProperty}
              disabled={
                isFileLoaded ||
                properties.length === 5 ||
                fileData?.fileStatus !== 'Open' ||
                fileData?.serviceStatus?.isReopenedByProduct
              }
              data-testid="FileInfoAddPropertyTop"
              plusIcon
              size="small"
            >
              Add Property
            </AgentNetButton>
          }
        />
        {isFileReady &&
          properties.map((property: propertyType, index: number) => {
            return (
              <Grid key={index}>
                <PropertyGroupWithAgentNetBase
                  isFileInformation={true}
                  propertyRefs={index === properties.length - 1 ? lastItemRef : propertiesRefs}
                  key={index}
                  properties={properties}
                  defaultProperty={fileDataDefaultValue.current?.properties}
                  handleGenerateReport={
                    isPropertyReportExceptionState(fileData?.propertyState || '') ? undefined : handleGenerateReport
                  }
                  handleTitleMonitoring={handleTitleMonitoring}
                  titleMonitoringInvitesByPropertyId={titleMonitoringInvitesByPropertyId}
                  isTitleMonitoringAvailable={isTitleMonitoringAvailable}
                  isLoadingTitleMonitoringInvites={isLoadingTitleMonitoringInvites}
                  handleValidateAddress={handleValidateAddress}
                  handlePropertyDelete={handlePropertyDelete}
                  setProperties={setProperties}
                  currentIndex={index}
                  value={property}
                  isFileReady={isFileReady}
                  disableState={true}
                  onChange={(newPropertyData: propertyType) => {
                    const tempPropertyList = properties;
                    if (
                      currentFile.fileId === fileData?.fileId &&
                      isFileReady &&
                      fileDataDefaultValue.current?.fileId === currentFileId
                    ) {
                      if (fileDataDefaultValue.current?.properties?.[index]) {
                        if (!fileDataDefaultValue.current?.properties?.[index].apn && !newPropertyData?.apn) {
                          delete newPropertyData.apn;
                        }
                        if (!fileDataDefaultValue.current?.properties?.[index].address1 && !newPropertyData?.address1) {
                          delete newPropertyData.address1;
                        }
                        if (!fileDataDefaultValue.current?.properties?.[index].address2 && !newPropertyData?.address2) {
                          delete newPropertyData.address2;
                        }
                        if (!fileDataDefaultValue.current?.properties?.[index].city && !newPropertyData?.city) {
                          delete newPropertyData.city;
                        }
                        if (
                          !fileDataDefaultValue.current?.properties?.[index].postalCode &&
                          !newPropertyData?.postalCode
                        ) {
                          delete newPropertyData.postalCode;
                        }
                        if (!newPropertyData?.county && !fileDataDefaultValue.current?.properties?.[index].county) {
                          delete newPropertyData.county;
                        }

                        // delete postalcode if not exist
                        // applicable when file is created from old application
                        // as postalcode is not required field there
                        if (
                          !newPropertyData?.postalCode &&
                          !fileDataDefaultValue.current?.properties?.[index].postalCode
                        ) {
                          delete newPropertyData.postalCode;
                        }

                        // Remove Unwanted Data
                        const exactTempPropertyList = _.cloneDeep(tempPropertyList[index]);
                        const exactDefaultPropertyList = _.cloneDeep(fileDataDefaultValue.current?.properties?.[index]);
                        const exactNewPropertyList = _.cloneDeep(newPropertyData);
                        delete exactTempPropertyList.expand;
                        delete exactDefaultPropertyList.expand;
                        delete exactNewPropertyList.expand;

                        delete exactTempPropertyList.propertyCustomId;
                        delete exactDefaultPropertyList.propertyCustomId;
                        delete exactNewPropertyList.propertyCustomId;

                        if (exactNewPropertyList?.county && exactDefaultPropertyList.county) {
                          exactNewPropertyList.county = exactNewPropertyList.county.toUpperCase();
                          exactDefaultPropertyList.county = exactDefaultPropertyList.county.toUpperCase();
                        }
                        if (exactTempPropertyList?.county && exactNewPropertyList.county) {
                          exactNewPropertyList.county = exactNewPropertyList.county.toUpperCase();
                          exactTempPropertyList.county = exactTempPropertyList.county.toUpperCase();
                        }

                        const isRerendered = _.isEqual(exactTempPropertyList, exactNewPropertyList);
                        const isPropertySame = _.isMatch(exactDefaultPropertyList, exactNewPropertyList);

                        if (!isPropertySame) {
                          setPropertiesUpdated(getFileApiStatus !== 'pending');
                          setUserHasInteracted(true);
                        } else if (!isRerendered) {
                          setPropertiesUpdated(false);
                          setUserHasInteracted(false);
                        }
                      } else {
                        setPropertiesUpdated(getFileApiStatus !== 'pending');
                        setUserHasInteracted(true);
                      }
                    }
                    tempPropertyList[index] = newPropertyData;
                    fileData && setFileData && setFileData({ ...fileData, properties: tempPropertyList });
                  }}
                  setIsFormValid={(valid: any) => {
                    handlePropertiesValidity(valid, property.propertyId ?? property.propertyCustomId);
                  }}
                  listIndex={index}
                  firstLoad={firstLoad}
                  isCreateProperty={false}
                  showAllValidation={submitAttempted}
                  isFirstPropertyStateOnly={true}
                  onShiftClickExpand={handleExpand}
                />
              </Grid>
            );
          })}
        {properties.length > 1 && (
          <AgentNetButton
            color="primary"
            size="small"
            variant="outlined"
            onClick={handleAddNewProperty}
            data-testid="FileInfoAddPropertyBottom"
            disabled={
              !(isFileReady && fileDataDefaultValue?.current?.fileId === currentFileId) ||
              properties.length === 5 ||
              fileData?.fileStatus !== 'Open' ||
              fileData?.serviceStatus?.isReopenedByProduct
            }
            className={classes.addPropertyBtn}
            plusIcon
          >
            Add Property
          </AgentNetButton>
        )}

        {PartiesSection}
      </Container>
      {/* Wil need to make this either a ternary or dynamic based on the property delete confirmation or file  save confirmation */}
      <AgentNetConfirmationDialog
        onConfirm={() => {
          if (currentPropertyId) {
            executeDeleteProperty().then(() => setOpenDeleteModal(false));
            setIsSubmitted(true);
          } else {
            handlePropertiesValidity(
              true,
              properties?.filter((propertyValue, propertyIndex) => propertyIndex === currentPropertyIndex)?.[0]
                ?.propertyCustomId,
            );

            const tempProperty =
              properties?.filter((propertyValue, propertyIndex) => propertyIndex !== currentPropertyIndex) ?? [];
            setProperties([...tempProperty]);
            checkPropertyUpdated(fileDataDefaultValue.current?.properties, tempProperty);
            setFileData && setFileData({ ...fileData, properties: [...tempProperty] });
            setOpenDeleteModal(false);
            setCurrentPropertyId('');
            showToaster(setShowDeletePropertyNotification);
          }
          setCurrentPropertyIndex(-1);
        }}
        disabled={isSubmitted}
        open={openDeleteModal}
        disableCrossIconClick={isSubmitted}
        onDismissAction={handleCloseDialog}
        dialogTitle="Delete Property"
        dialogBtnContent="Delete Property"
        qaAttrPrefix="ConfirmationProperty"
        dialogText="Are you sure you want to delete this property? You will not be able to recover it."
      />
      <AgentNetConfirmationDialog
        onConfirm={() => {
          handleSaveConfirm().then();
        }}
        open={openSaveModal || false}
        disabled={saveConfirmFileLoader}
        disableCrossIconClick={saveConfirmFileLoader}
        onDismissAction={() => {
          /* Handle Don't Save */
          setOpenSaveModal && setOpenSaveModal(false);
          if (setFileData && fileDataDefaultValue?.current) {
            // Reset the data to clean
            setFileData(_.cloneDeep(fileDataDefaultValue.current) as fileInfoType);
            // TODO: We shouldn't have a separate state for this. Consider refactoring.
            setCurrentFile(_.cloneDeep(fileDataDefaultValue.current) as fileInfoType);
          }
          setFileInfoError([]);
          const isDrawerTriedToOpen = isCreateDrawerClicked || isRatesAndFeeDrawerClicked;
          if (isDrawerTriedToOpen) {
            if (isCreateDrawerClicked) {
              setOpenCreateDrawer?.(true);
            } else {
              setOpenRatesAndFeeDrawer?.(true);
            }
            setIsFormDirty?.(false);
            setIsCreateDrawerClicked?.(false);
            setRatesAndFeeDrawerClicked?.(false);
            resetFileInfo();
            if (fileDataDefaultValue.current && setFileData)
              setFileData(_.cloneDeep(fileDataDefaultValue.current) as fileInfoType);
          } else if (logoutAttempted) {
            setIsFormDirty && setIsFormDirty(false);
            setLogoutAttempted && setLogoutAttempted(false);
            signOut().then();
            sessionStorage.clear();
            localStorage.clear();
          } else {
            navigate();
          }
        }}
        onExitButtonAction={() => {
          setOpenSaveModal?.(false);
          setIsFormDirty?.(true);
          setLogoutAttempted?.(false);
          setIsCreateDrawerClicked?.(false);
          setRatesAndFeeDrawerClicked?.(false);
          setTabValue && setTabValue('0');
        }}
        dialogTitle="Save Confirmation"
        dialogBtnContent="Save"
        secondaryActionBtnContent="Don't Save"
        dialogText="You have made changes to information on this page. Do you want to save the changes before you proceed?"
        disableBackDropClick
        qaAttrPrefix="ConfirmationSave"
      />

      <AgentNetConfirmationDialog
        onConfirm={() => {
          if (setFileData && fileDataDefaultValue?.current) {
            resetValidation();
            // Reset the data to clean
            setFileData(_.cloneDeep(fileDataDefaultValue.current) as fileInfoType);
            setCurrentFile(_.cloneDeep(fileDataDefaultValue.current) as fileInfoType);
            setPropertiesUpdated(false);
            setPartiesUpdated(false);
            setFileChanged(false);
            setFileUpdated(true);
            setSubmitAttempted(false);
            disableInputs();
          }
          toggleCancelModel();
        }}
        open={cancelModel || false}
        onDismissAction={toggleCancelModel}
        onExitButtonAction={toggleCancelModel}
        dialogTitle="Warning!"
        dialogBtnContent="Continue"
        secondaryActionBtnContent="Cancel"
        dialogText="Continue will revert any unsaved changes"
        disableBackDropClick
        qaAttrPrefix="Confirmation"
      />
      {Object.keys(verifiedPropertiesByIndex).length > 0 && (
        <VerifiedPropertiesDrawer
          propertiesByIndex={verifiedPropertiesByIndex}
          sourcePage="edit"
          submitAction={() => {
            if (isSaveValidate) {
              executeSaveFile().then();
            } else {
              setFileChanged(true);
            }
            setVerifiedPropertiesByIndex({});
          }}
        />
      )}
      <div className={classes.sdnResultsLoadingContainer}>
        <>
          <LoadingSpinner status={isGetDocument ? 'pending' : 'success'} variant="linear" />
        </>
      </div>
      {BottomFooter}
    </div>
  );
};

export default FileInformation;
